import { CHAT_RESULT_EXPERIMENT, CHAT_RESULT_VARIANTS, type ChatResultVariant } from '~/constants/chatResultExperiment'

/**
 * A/B/C test: how the chat's result sells what comes after the CV.
 *
 * - `a`: the result as it was, the CV and a column of offers under "now what?".
 * - `b`: a path over the result (CV, tailoring, interview, web CV) and the
 *   column as numbered steps, the Job Search Pack stacked from our own prices.
 * - `c`: one step at a time. A sticky panel next to the CV (on phones a bar
 *   at the bottom of the screen) shows only the next thing to do: download
 *   it free with the posting field right under it, then the tailoring, then
 *   one choice of free / this CV tailored / the pack. The interview prep
 *   comes after the tailored CV is bought.
 *
 * The variant is a hash of the browser's analytics id, so a visitor keeps it
 * on every CV and reload; `?result=a|b|c` forces one for a look at it. Its
 * events carry `result_experiment` and `result_variant`; /admin/result-test
 * compares the three by what they sold per CV shown.
 */

const isVariant = (value: unknown): value is ChatResultVariant => (CHAT_RESULT_VARIANTS as readonly unknown[]).includes(value)

/** Small, stable string hash (FNV-1a); only has to split visitors evenly. */
function hash(value: string): number {
  let h = 0x811C9DC5
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

export function useChatResultExperiment() {
  const journey = useJourney()
  const route = useRoute()
  const variant = useState<ChatResultVariant | null>('chat-result-variant', () => null)

  /** This visitor's variant; without an analytics id (tracking off) a coin toss for the page. */
  function assign(): ChatResultVariant {
    if (variant.value) return variant.value
    const forced = route.query.result
    if (isVariant(forced)) return (variant.value = forced)
    const id = journey.visitorId()
    const bucket = id ? hash(`${CHAT_RESULT_EXPERIMENT}:${id}`) % 3 : Math.floor(Math.random() * 3)
    return (variant.value = CHAT_RESULT_VARIANTS[bucket]!)
  }

  /** What the offers add to their journey events, so each can be put on its variant. */
  const trackingData = (): Record<string, unknown> => ({
    result_experiment: CHAT_RESULT_EXPERIMENT,
    result_variant: assign(),
  })

  /** A CV's result was shown in its variant: the experiment's denominator. */
  function trackExposure(data: { generationId: string | null, source: string }) {
    journey.track('chat_result_exposure', {
      generation_id: data.generationId,
      source: data.source,
      ...trackingData(),
    })
  }

  return { variant, assign, trackingData, trackExposure }
}
